import {
  CallResult,
  FileMeta,
  FilesContents,
  FilesMeta,
  IsClosed,
  SyncLock,
  TableName,
  TipFile,
  User,
  Walk,
} from '../store/dbTypes'
import { fbSetup } from './fbFunctions'
import {
  deleteObject,
  getBytes,
  getDownloadURL,
  getMetadata,
  list,
  listAll,
  ListResult,
  ref,
  StorageReference,
  uploadBytes,
  uploadString,
} from 'firebase/storage'
import { constants } from '../config'
import { UsersLocationFiles } from '../Tracker/Tracker.types'
import { format, startOfToday, subDays } from 'date-fns'
import { ERROR_GETTING_CONTENT } from './syncWithRemote'
import { collapseText } from './utils'

let FIREBASE_FOLDER_PATH = `${constants.ENVIRONMENT}/`

export const getFbStorageFilenames = async (
  table: TableName | SyncLock | IsClosed | TipFile | 'table-backup'
): Promise<string[]> => {
  try {
    const { fbStorage } = await fbSetup()

    const folderPrefix =
      table === 'table-backup'
        ? `${FIREBASE_FOLDER_PATH}/dexie/backups/`
        : `${FIREBASE_FOLDER_PATH}/dexie/`
    const filePrefix = table === 'table-backup' ? `backup-` : `${table}-`

    // Firebase: build a folder ref for 1 folder, but not subfolders
    const folderRef = ref(fbStorage, folderPrefix)

    // Firebase: list all items (one level only) - not suitable for large lists!
    const listResult = await listAll(folderRef)

    // Filter by prefix (Firebase does not support server-side prefix filtering)
    const matchingItems = listResult.items.filter((item) =>
      item.name.startsWith(filePrefix)
    )

    // Fetch metadata for timestamps (Firebase does not return timestamps in listAll)
    const itemsWithMeta = await Promise.all(
      matchingItems.map(async (itemRef) => {
        const meta = await getMetadata(itemRef)
        return {
          ref: itemRef,
          updated: new Date(meta.updated),
          name: itemRef.name,
        }
      })
    )

    // Sort newest first
    itemsWithMeta.sort((a, b) => b.updated.getTime() - a.updated.getTime())

    // Extract IDs from filenames
    const idsList = itemsWithMeta.map((item) =>
      getIdFromFile({ Key: item.name }, filePrefix)
    )

    return idsList
  } catch (error) {
    const errorMessage =
      (error as any).err || (error as any).message || 'Unknown error'
    return ['List Error', errorMessage]
  }
}

const getIdFromFile = (f: any, prefix: string) => {
  const rest = f.Key.slice(prefix.length)
  const idArray = rest.split('.')
  return idArray[0]
}

function hasSensibleContent(fileContent: any) {
  let hasContent = false
  // let tableName = 'UnknownTable'
  if (!fileContent) {
    hasContent = false
  } else if (Array.isArray(fileContent) && fileContent?.length) {
    hasContent = true
  } else if (
    typeof fileContent === 'object' &&
    Object.keys(fileContent).length
  ) {
    hasContent = true
    // tableName = fileContent.tableName || 'UnspecifiedTable'
  } else if (typeof fileContent === 'string' && fileContent.length) {
    hasContent = true
  }
  return hasContent
}

export const createFbStorageJsonFile = async (
  table: TableName | SyncLock | FilesMeta | IsClosed | TipFile | 'table-backup',
  id: string,
  fileContent: any
): Promise<CallResult> => {
  try {
    // TODO remove table-backup
    // if (table === 'table-backup') {
    //   return Promise.resolve({
    //     status: 'error',
    //     isSuccess: false,
    //     message: `Error - no remote storage for ${table}`,
    //   })
    // }

    const { fbStorage } = await fbSetup()

    // Sensible content?
    let hasContent = hasSensibleContent(fileContent)
    if (!hasContent) {
      return Promise.resolve({
        status: 'error',
        isSuccess: false,
        message: `Error - Will not save ${table} with incorrect content`,
      })
    }

    // Define the actual file path
    const userName = ''
    const fileName =
      table === 'table-backup'
        ? `backup-${fileContent.tableName}-${userName || 'UnspecifiedUser'}-${
            fileContent.id
          }`
        : table === 'files-meta'
        ? `${id}-meta`
        : `${table}-${id}`
    const fileType = '.json'
    const subFolderPath =
      table === 'table-backup'
        ? 'dexie/backups/'
        : table === 'files-meta'
        ? 'files/'
        : 'dexie/'
    const filePath = `${FIREBASE_FOLDER_PATH}${subFolderPath}${fileName}${fileType}`

    // const folderPrefix =
    //   table === 'table-backup'
    //     ? `${FIREBASE_FOLDER_PATH}/dexie/backups/`
    //     : `${FIREBASE_FOLDER_PATH}/dexie/`
    // const filePrefix = table === 'table-backup' ? `backup-` : `${table}-`
    // const fileName = `${filePrefix}${id}.json`
    // const filePath = `${folderPrefix}${fileName}`

    const fileRef = ref(fbStorage, filePath)

    const contentString =
      typeof fileContent === 'string'
        ? fileContent
        : JSON.stringify(fileContent, null, 2)

    await uploadString(fileRef, contentString, 'raw', {
      contentType: 'application/json',
    })

    return {
      status: 'success',
      isSuccess: true,
      message: `File ${fileName}${fileType} uploaded to FB Storage`,
    }
  } catch (error) {
    const errorMessage =
      (error as any).err || (error as any).message || 'Unknown error'
    return {
      status: 'error',
      isSuccess: false,
      message: errorMessage,
    }
  }
}

export const createFbStorageFile = async (
  id: string,
  fileContent: File
): Promise<string> => {
  try {
    const { fbStorage } = await fbSetup()

    const newFilename = id + '-contents'
    const fileNameLastPart = fileContent.name.split('.').pop()
    const fileExtension = fileNameLastPart ? `.${fileNameLastPart}` : ''
    const filePath = `${FIREBASE_FOLDER_PATH}files/${newFilename}${fileExtension}`

    const fileRef = ref(fbStorage, filePath)

    await uploadBytes(fileRef, fileContent, {
      contentType: fileContent.type || undefined,
    })

    return Promise.resolve('uploaded')
  } catch (error) {
    const errorMessage =
      (error as any).err || (error as any).message || 'Unknown error'
    return Promise.resolve('Error: ' + errorMessage)
  }
}

export const deleteFbStorageFile = async (
  table: TableName | SyncLock | FilesMeta | FilesContents | IsClosed | TipFile,
  data: string | FileMeta
): Promise<string> => {
  try {
    const { fbStorage } = await fbSetup()

    let id = ''
    let fileType = ''
    let filename = ''
    let subFolderPath = ''

    if (table === 'files-meta' || table === 'files-contents') {
      const fileMeta = data as FileMeta
      id = fileMeta.fileId
      const fileTypeArray = fileMeta.fileNameType.split('.')
      fileType =
        table === 'files-meta' ? `.json` : '.' + (fileTypeArray.pop() ?? '')
      filename = table === 'files-meta' ? `${id}-meta` : `${id}-contents`
      subFolderPath = 'files/'
    } else {
      id = data as string
      fileType = '.json'
      filename = `${table}-${id}`
      subFolderPath = 'dexie/'
    }

    const filePath = `${FIREBASE_FOLDER_PATH}${subFolderPath}${filename}${fileType}`
    const fileRef = ref(fbStorage, filePath)

    await deleteObject(fileRef)
    return ''
  } catch (error) {
    const errorMessage =
      (error as any).err || (error as any).message || 'Unknown error'
    return 'Error deleting: ' + errorMessage
  }
}

export const downloadFbStorageFile = async (
  fileMeta: FileMeta
): Promise<void> => {
  // Find the name and build the storage path
  const fileTypeArray = fileMeta.fileNameType.split('.')
  const fileType = '.' + fileTypeArray.pop()
  const fileSubPathName = `files/${fileMeta.fileId}-contents` + fileType
  const filePath = `${FIREBASE_FOLDER_PATH}${fileSubPathName}`

  const { fbStorage } = await fbSetup()
  const fileRef = ref(fbStorage, filePath)

  try {
    const url = await getDownloadURL(fileRef)

    // This can be downloaded directly:
    const xhr = new XMLHttpRequest()
    xhr.responseType = 'blob'
    xhr.onload = (event) => {
      const blob = xhr.response
      const objectUrl = URL.createObjectURL(blob)
      const hiddenLink = document.createElement('a')
      hiddenLink.href = objectUrl
      hiddenLink.download = fileMeta.fileNameType
      hiddenLink.click()
      window.URL.revokeObjectURL(hiddenLink.href)
    }
    xhr.open('GET', url)
    xhr.send()
  } catch (error) {
    // Silently fail; callers can log if needed
    console.error('Error downloading from Firebase Storage:', error)
  }
}

export const getFbStorageJsonFileContents = async (
  table:
    | TableName
    | SyncLock
    | FilesMeta
    | TipFile
    | FilesContents
    | 'image'
    | 'table-backup'
    | 'location-data',
  id: string,
  fileType?: string
): Promise<Walk[] | User[] | string | FileMeta | Blob | any> => {
  try {
    const fileSubPathName =
      table === 'table-backup'
        ? `dexie/backups/backup-${id}.json`
        : table === 'files-meta'
        ? `files/${id}-meta.json`
        : table === 'files-contents' || table === 'image'
        ? `files/${id}-contents` + (fileType ?? '')
        : `dexie/${table}-${id}.json`

    const filePath =
      table === 'location-data'
        ? id
        : `${FIREBASE_FOLDER_PATH}${fileSubPathName}`

    const { fbStorage } = await fbSetup()
    const fileRef = ref(fbStorage, filePath)
    return await getDataFromFileRef(filePath, fileRef)
  } catch (error) {
    return ERROR_GETTING_CONTENT
  }
}

function arrayBufferToJson(arrayBuffer: ArrayBuffer) {
  try {
    const jsonString = new TextDecoder('utf-8').decode(arrayBuffer)
    const jsonObject = JSON.parse(jsonString)
    return jsonObject
  } catch (e) {
    console.error('Failed to convert ArrayBuffer to JSON:', e)
    return null
  }
}

async function getDataFromFileRef(filePath: string, fileRef: StorageReference) {
  const ab = await getBytes(fileRef)
  if (filePath.endsWith('.json')) {
    return arrayBufferToJson(ab)
  }
  throw new Error('Unexpected data format received ie not JSON')
}

export const getFbStorageTrackedUserLocations = async (
  userName: string,
  date: Date,
  setNrLocationFiles: any
): Promise<UsersLocationFiles> => {
  if (!userName) return {}

  const { fbStorage } = await fbSetup()

  try {
    const name = collapseText(userName, true)
    const _date = format(date, 'yyyy-MM-dd')
    const folderPrefix = `${FIREBASE_FOLDER_PATH}location/${name}/${_date}`
    const filePrefix = `location_${_date}T`

    // List all objects under the users folder
    console.time('list')
    const folderRef = ref(fbStorage, folderPrefix)
    let pageToken = null
    let errorIs = ''
    const allItems: any[] = []
    do {
      try {
        const listResult: ListResult = await list(folderRef, {
          maxResults: 100,
          pageToken,
        })
        allItems.push(...listResult.items)
        setNrLocationFiles(allItems.length)
        pageToken = listResult.nextPageToken
      } catch (error: any) {
        console.error(`Error listing location files in ${folderPrefix}:`, error)
        pageToken = null // Stop the loop on error
        errorIs = error.message || error.toString()
        break
      }
    } while (Boolean(pageToken))

    // Filter only relevant location files (those starting with location prefix)
    const allLocationFiles =
      allItems.filter((item) =>
        item.fullPath.startsWith(folderPrefix + '/' + filePrefix)
      ) || []

    // Filter files in a sensible timeframe e.g. 7 days
    const sinceDays = 7
    const recentLocatorFiles =
      (
        await Promise.all(
          allLocationFiles.map(async (itemRef) => {
            try {
              const meta = await getMetadata(itemRef)
              return { itemRef, updated: meta.timeCreated || meta.updated }
            } catch (error: any) {
              console.error(
                `Error getting metadata for ${itemRef.fullPath}:`,
                error
              )
              errorIs = error.message || error.toString()
              return { itemRef, updated: null }
            }
          })
        )
      ).filter(({ updated }) => {
        if (!updated) return false
        const lastMidnight = startOfToday()
        const thresholdOldest = subDays(lastMidnight, sinceDays).toISOString()
        const createdAt = new Date(updated).toISOString()
        return createdAt >= thresholdOldest // && createdAt <= thresholdNewest
      }) || []

    // Sort on time
    recentLocatorFiles.sort((a, b) =>
      a.itemRef.fullPath.localeCompare(b.itemRef.fullPath)
    )

    // Split per user
    const locationsPerUser: UsersLocationFiles = {}
    for (const { itemRef } of recentLocatorFiles) {
      const key = itemRef.fullPath
      if (!key) continue
      const parts = key.split('/')
      const user = parts[2] // local/location/<user>/...
      if (!user) continue
      if (!locationsPerUser[user]) locationsPerUser[user] = []
      locationsPerUser[user].push(key)
    }

    if (errorIs) {
      const user = collapseText(userName, true)
      if (!locationsPerUser[user]) locationsPerUser[user] = []
      locationsPerUser[user].push('ERROR: ' + errorIs)
    }

    return locationsPerUser
  } catch (error: any) {
    const errorMessage = error.message || error.toString()
    console.error(
      'Error getting user locations from firebase storage:',
      errorMessage
    )
    const user = collapseText(userName, true)
    return { [user]: ['ERROR: ' + errorMessage] } // Return error file name for the user on general error
  }
}
